// In-App Ads Headliner Configuration - Alpine.js Component
// Handles configuration for Headliner in-app advertisements

import { createUppyUploader } from '../shared/custom_podpromote_uppy_uploader.js';

document.addEventListener('alpine:init', () => {
  Alpine.data('in_app_ads_headliner_x_data', () => ({
    // Component state
    isSubmitting: false,
    formData: {
      visual_asset_file: null,
      targeting: {
        locations: []
      },
      episodes: [
        {
          id: '',
          release_date: '',
          apple_link: '',
          spotify_link: ''
        }
      ]
    },

    // Input variables for text fields
    newLocation: '',
    
    // Pill management functions for location targeting
    addLocation() {
      if (this.newLocation && this.newLocation.trim()) {
        const location = this.formatTextInput(this.newLocation.trim());
        if (location && !this.formData.targeting.locations.includes(location)) {
          // Check maximum limit
          if (this.formData.targeting.locations.length >= 10) {
            alert('Maximum of 10 locations allowed');
            return;
          }
          this.formData.targeting.locations.push(location);
        }
        this.newLocation = '';
      }
    },

    removeLocation(location) {
      const index = this.formData.targeting.locations.indexOf(location);
      if (index > -1) {
        this.formData.targeting.locations.splice(index, 1);
      }
    },

    // Format text input: only allow letters, numbers, hyphens, and spaces between words
    formatTextInput(text) {
      return text
        .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters except letters, numbers, spaces, and hyphens
        .replace(/^[\s-]+|[\s-]+$/g, '') // Remove leading/trailing spaces and hyphens
        .replace(/[\s-]+/g, ' ') // Replace multiple spaces/hyphens with single space
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .trim();
    },


    
    // Show upload success message (copied from Listen Network approach)
    showUploadSuccess() {
      // Show the upload status div
      const statusElement = document.getElementById('visual-asset-upload-status');
      if (statusElement) {
        statusElement.classList.remove('hidden');
      }
    },
    
    // Show image preview
    showImagePreview(imageUrl) {
      const previewDiv = document.getElementById('image-preview');
      const previewImage = document.getElementById('preview-image');
      
      if (previewDiv && previewImage) {
        previewImage.src = imageUrl;
        previewDiv.classList.remove('hidden');
      }
    },
    
    // Validation state
    errors: {},
    
    init() {
      this.loadSavedConfiguration();
      this.initializeUppy();
      
      // Set up cleanup when component is destroyed
      this.$cleanup = () => {
        this.cleanup();
      };
    },
    
    // Load saved configuration from backend
    loadSavedConfiguration() {
      try {
        const savedDataDiv = this.$refs.savedConfigData;
        
        if (savedDataDiv && savedDataDiv.dataset.savedConfig) {
          const savedConfig = JSON.parse(savedDataDiv.dataset.savedConfig);
          
          if (savedConfig) {
            // Load targeting data
            if (savedConfig.targeting) {
              // Handle both new array format and old string format for backward compatibility
              if (savedConfig.targeting.locations && Array.isArray(savedConfig.targeting.locations)) {
                this.formData.targeting.locations = savedConfig.targeting.locations;
              } else if (savedConfig.targeting.location && savedConfig.targeting.location !== '') {
                // Convert old string format to new array format
                this.formData.targeting.locations = [savedConfig.targeting.location];
              }
            }
            
            // Load visual asset file data
            if (savedConfig.visual_asset_file && savedConfig.visual_asset_file.url && savedConfig.visual_asset_file.url !== 'unknown') {
              this.formData.visual_asset_file = savedConfig.visual_asset_file;
              
              // Also update global backup
              window.headlinerVisualAssetFile = this.formData.visual_asset_file;
              
              // Show upload status and image preview for previously uploaded file after DOM is ready
              this.$nextTick(() => {
                this.showUploadSuccess();
                this.showImagePreview(this.formData.visual_asset_file.url);
              });
            }
            
            // Load episodes data
            if (savedConfig.episodes && savedConfig.episodes.length > 0) {
              this.formData.episodes = savedConfig.episodes.map(episode => ({
                id: episode.episode_id || episode.id || '',
                release_date: episode.release_date || '',
                apple_link: episode.apple_link || '',
                spotify_link: episode.spotify_link || ''
              }));
              
              // Populate episode select dropdowns with saved data
              this.$nextTick(() => {
                this.populateEpisodeSelects();
              });
            }
          }
        }
      } catch (error) {
        // Silently handle configuration loading errors
      }
    },
    
    // Populate episode select dropdowns with saved data
    populateEpisodeSelects() {
      this.formData.episodes.forEach((episode, index) => {
        if (episode.id) {
          const selectElement = document.querySelector('.episode-select');
          
          if (selectElement) {
            selectElement.value = episode.id;
          }
        }
      });
    },
    
    // Cleanup when component is destroyed
    cleanup() {
      try {
        if (window.uppyInstances && window.uppyInstances['headliner-visual-asset-uploader']) {
          const uppyInstance = window.uppyInstances['headliner-visual-asset-uploader'];
          
          // Clean up the Uppy instance
          if (uppyInstance && typeof uppyInstance.close === 'function') {
            uppyInstance.close();
          }
          
          // Remove from global registry
          delete window.uppyInstances['headliner-visual-asset-uploader'];
        }
      } catch (error) {
        // Silently handle cleanup errors
      }
    },
    
    // Initialize Uppy uploader
    initializeUppy() {
      try {
        // Check if Uppy instance already exists
        if (window.uppyInstances && window.uppyInstances['headliner-visual-asset-uploader']) {
          return;
        }

        const showId = this.getShowId();
        const campaignId = this.getCampaignId();
        
        if (!showId || !campaignId) {
          console.error('Show ID or Campaign ID not found');
          return;
        }

        const campaignTacticId = this.getCampaignTacticId();
        if (!campaignTacticId) {
          console.error('Campaign Tactic ID not found');
          return;
        }

        // Initialize Uppy uploader using the modular approach
        const uppyInstance = createUppyUploader({
          id: 'headliner-visual-asset-uploader',
          show_id: showId,
          campaign_id: campaignId,
          campaign_tactic_id: campaignTacticId,
          trigger_id: '#headliner-visual-asset-upload-trigger',
          max_file_size: 2 * 1024 * 1024, // 2MB
          allowed_file_types: ['image/jpeg', 'image/jpg', 'image/png'],
          max_number_of_files: 1,
          auto_proceed: false, // Set to false to prevent automatic upload
          close_after_finish: true,
          min_dimensions: { width: 300, height: 250 },
          max_dimensions: { width: 300, height: 250 },
          note: 'Accepted files: JPG, PNG. Maximum size: 2MB. Dimensions: 300x250px.',
          on_complete: (result) => {
            this.handleUploadComplete(result);
          },
          on_error: (file, error, response) => {
            this.handleUploadError(file, error, response);
          },
          alpine: this
        });
        
        // Store the instance globally to prevent re-initialization
        if (!window.uppyInstances) {
          window.uppyInstances = {};
        }
        window.uppyInstances['headliner-visual-asset-uploader'] = uppyInstance;
        
      } catch (error) {
        // Silently handle Uppy initialization errors
      }
    },
    
    getShowId() {
      const urlParts = window.location.pathname.split('/');
      const showIndex = urlParts.indexOf('shows');
      if (showIndex !== -1 && showIndex + 1 < urlParts.length) {
        return urlParts[showIndex + 1];
      }
      return null;
    },

    getCampaignId() {
      const urlParts = window.location.pathname.split('/');
      const campaignIndex = urlParts.indexOf('campaigns');
      if (campaignIndex !== -1 && campaignIndex + 1 < urlParts.length) {
        return urlParts[campaignIndex + 1];
      }
      return null;
    },

    getCampaignTacticId() {
      // Try to get from the routes div first
      const routesDiv = this.$refs.routes;
      if (routesDiv && routesDiv.dataset.campaignTacticId) {
        return routesDiv.dataset.campaignTacticId;
      }
      
      // Fallback to URL parsing if needed
      const urlParts = window.location.pathname.split('/');
      const tacticIndex = urlParts.indexOf('campaign-tactics');
      if (tacticIndex !== -1 && tacticIndex + 1 < urlParts.length) {
        return urlParts[tacticIndex + 1];
      }
      
      return null;
    },
    
    // Handle file upload completion
    handleUploadComplete(result) {
      if (result.successful && result.successful.length > 0) {
        const file = result.successful[0];
        
        // Parse the response body to get the S3 data
        let responseData;
        try {
          responseData = typeof file.response.body === 'string' ? JSON.parse(file.response.body) : file.response.body;
        } catch (e) {
          responseData = file.response.body;
        }
        
        // Extract S3 data from response (matching the pattern from other working partials)
        const s3Key = responseData?.key || responseData?.s3_key || 'unknown';
        const s3Url = responseData?.uploadURL || responseData?.url || responseData?.s3_url || 'unknown';
        
        // Store complete file data in Alpine.js formData
        this.formData.visual_asset_file = {
          name: file.name,
          size: file.size,
          type: file.type,
          s3_key: s3Key,
          url: s3Url,
          filename: responseData?.filename || file.name
        };
        
        // Also store in global variable as backup (like AudienceLift does)
        window.headlinerVisualAssetFile = this.formData.visual_asset_file;
        
        // Store file data in hidden input
        const hiddenInput = document.getElementById('visual_asset_file');
        if (hiddenInput) {
          hiddenInput.value = JSON.stringify(this.formData.visual_asset_file);
        }
        
        // Show upload success message and image preview
        this.$nextTick(() => {
          this.showUploadSuccess();
          this.showImagePreview(s3Url);
        });
      }
    },
    
    // Handle upload errors
    handleUploadError(file, error, response) {
      // Show error message in upload status
      let errorMessage = error.message || 'Unknown error occurred';
      
      if (error.message && error.message.includes('size')) {
        errorMessage = `File Size Error: ${error.message}\n\nPlease upload a file smaller than 2MB.`;
      } else if (error.message && error.message.includes('dimensions')) {
        errorMessage = `File Dimensions Error: ${error.message}\n\nPlease upload a 300x250px image file.`;
      } else if (error.message && error.message.includes('type')) {
        errorMessage = `File Type Error: ${error.message}\n\nPlease upload a 300x250px JPG or PNG file.`;
      }
      
      // Show alert for validation errors
      alert(errorMessage);
    },
    
    // Handle episode selection
    handleEpisodeSelection(episodeId, index) {
      if (!episodeId) {
        this.formData.episodes[index] = {
          id: '',
          release_date: '',
          apple_link: '',
          spotify_link: ''
        };
        return;
      }
      
      // Find the selected episode from the episodes data
      const episodesData = window.episodesData || [];
      const selectedEpisode = episodesData.find(ep => ep.id === episodeId);
      
      if (selectedEpisode) {
        // Parse and format the release date
        let formattedDate = '';
        if (selectedEpisode.pub_date) {
          try {
            const date = new Date(selectedEpisode.pub_date);
            if (!isNaN(date.getTime())) {
              formattedDate = date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
            }
          } catch (error) {
            console.error('Error parsing date:', error);
            formattedDate = selectedEpisode.pub_date;
          }
        }
        
        this.formData.episodes[index] = {
          id: selectedEpisode.id,
          release_date: formattedDate,
          apple_link: selectedEpisode.distribution_platforms?.itunes_url || '',
          spotify_link: selectedEpisode.distribution_platforms?.spotify_url || ''
        };
      }
    },
    
    // Check if file is uploaded
    hasFileUploaded() {
      // Use the same validation logic as AudienceLift for consistency
      const hasFile = this.formData.visual_asset_file && 
                     this.formData.visual_asset_file.url && 
                     this.formData.visual_asset_file.url !== 'unknown';
      
      // If local formData doesn't have the file, check global backup
      if (!hasFile && window.headlinerVisualAssetFile) {
        const globalHasFile = window.headlinerVisualAssetFile.url && 
                             window.headlinerVisualAssetFile.url !== 'unknown';
        return globalHasFile;
      }
      
      return hasFile;
    },
    
    // Clear uploaded file data
    clearFileData() {
      this.formData.visual_asset_file = null;
      const hiddenInput = document.getElementById('visual_asset_file');
      if (hiddenInput) {
        hiddenInput.value = '';
      }
    },
    
    // Validate form data
    validateForm() {
      this.errors = {};
      
      // Check if visual asset file is uploaded
      if (!this.hasFileUploaded()) {
        this.errors.visual_asset_file = 'Visual asset is required';
      }
      
      // Check if at least one location is provided
      if (this.formData.targeting.locations.length === 0) {
        this.errors.location = 'At least one location is required';
      }
      
      // Check if episode is selected
      if (!this.formData.episodes[0]?.id) {
        this.errors.episode = 'Episode selection is required';
      }
      
      return Object.keys(this.errors).length === 0;
    },
    
    // Save form configuration
    saveAsDraft() {
      try {
        // Prepare data for submission
        // Use global backup if local formData is missing the file (fixes Alpine.js reactivity issues)
        const visualAssetFile = this.formData.visual_asset_file || window.headlinerVisualAssetFile;
        
        const formDataToSubmit = {
          targeting: {
            locations: this.formData.targeting.locations
          },
          episodes: this.formData.episodes.filter(ep => ep.id).map(ep => ({
            episode_id: ep.id,
            release_date: ep.release_date,
            apple_link: ep.apple_link,
            spotify_link: ep.spotify_link
          })),
          visual_asset_file: visualAssetFile
        };
        
        // Get the save as draft link from the routes template
        const routesTemplate = this.$refs.routes;
        const saveAsDraftLink = routesTemplate?.dataset?.saveAsDraftLink;
        
        if (!saveAsDraftLink) {
          alert('Save as draft link not found. Please refresh the page and try again.');
          return;
        }
        
        // Create a hidden form and submit it
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = saveAsDraftLink;
        
        // Add CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (csrfToken) {
          const csrfInput = document.createElement('input');
          csrfInput.type = 'hidden';
          csrfInput.name = 'authenticity_token';
          csrfInput.value = csrfToken;
          form.appendChild(csrfInput);
        }
        
        // Add method override for PATCH
        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        methodInput.value = 'PATCH';
        form.appendChild(methodInput);
        
        // Add form data as JSON
        const formDataInput = document.createElement('input');
        formDataInput.type = 'hidden';
        formDataInput.name = 'form_data';
        formDataInput.value = JSON.stringify(formDataToSubmit);
        form.appendChild(formDataInput);
        
        // Add to DOM and submit
        document.body.appendChild(form);
        form.submit();
      } catch (error) {
        alert('Error saving as draft. Please try again.');
      }
    },

    saveForm() {
      try {
        // Validate form first
        if (!this.validateForm()) {
          // Show first error
          const firstError = Object.values(this.errors)[0];
          alert(firstError);
          return;
        }
        
        // Prepare data for submission
        // Use global backup if local formData is missing the file (fixes Alpine.js reactivity issues)
        const visualAssetFile = this.formData.visual_asset_file || window.headlinerVisualAssetFile;
        
        const formDataToSubmit = {
          targeting: {
            locations: this.formData.targeting.locations
          },
          episodes: this.formData.episodes.filter(ep => ep.id).map(ep => ({
            episode_id: ep.id,
            release_date: ep.release_date,
            apple_link: ep.apple_link,
            spotify_link: ep.spotify_link
          })),
          visual_asset_file: visualAssetFile
        };
        
        // Get the submit link from the routes template
        const routesTemplate = this.$refs.routes;
        const submitLink = routesTemplate?.dataset?.submitLink;
        
        if (!submitLink) {
          alert('Submit link not found. Please refresh the page and try again.');
          return;
        }
        
        // Create a hidden form and submit it
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = submitLink;
        
        // Add CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (csrfToken) {
          const csrfInput = document.createElement('input');
          csrfInput.type = 'hidden';
          csrfInput.name = 'authenticity_token';
          csrfInput.value = csrfToken;
          form.appendChild(csrfInput);
        }
        
        // Add method override for PATCH
        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        methodInput.value = 'PATCH';
        form.appendChild(methodInput);
        
        // Add form data as JSON
        const formDataInput = document.createElement('input');
        formDataInput.type = 'hidden';
        formDataInput.name = 'form_data';
        formDataInput.value = JSON.stringify(formDataToSubmit);
        form.appendChild(formDataInput);
        
        // Add to DOM and submit
        document.body.appendChild(form);
        form.submit();
      } catch (error) {
        alert('Error saving configuration. Please try again.');
      }
    }
  }));
});
